import type { Bible, CanonStatus, ValidationError } from "./types";
import { getMoment, isBefore } from "./engine";

const ID_RE = /^[a-z0-9][a-z0-9:-]*$/;
const STATUSES: CanonStatus[] = [
  "canon",
  "draft",
  "verify",
  "legacy",
  "contradiction",
  "rejected",
];

function err(path: string, message: string): ValidationError {
  return { path, message };
}

export function validateBible(data: unknown): ValidationError[] {
  const errors: ValidationError[] = [];
  if (!data || typeof data !== "object") {
    return [err("", "Le document doit être un objet JSON.")];
  }
  const bible = data as Partial<Bible>;
  const required = [
    "schemaVersion",
    "meta",
    "books",
    "branches",
    "moments",
    "characters",
    "events",
    "relationships",
    "secrets",
    "continuityIssues",
  ] as const;
  for (const key of required) {
    if (!(key in bible) || bible[key] == null) errors.push(err(key, "Champ obligatoire manquant."));
  }
  if (typeof bible.schemaVersion !== "number" || bible.schemaVersion < 1) {
    errors.push(err("schemaVersion", "Doit être un entier ≥ 1."));
  }
  if (!bible.meta || typeof bible.meta !== "object") {
    errors.push(err("meta", "Objet meta manquant."));
  } else {
    if (!bible.meta.title) errors.push(err("meta.title", "Titre obligatoire."));
    if (!bible.meta.updatedAt) errors.push(err("meta.updatedAt", "updatedAt obligatoire."));
  }

  const arrays: Array<keyof Bible> = [
    "books",
    "branches",
    "moments",
    "characters",
    "events",
    "relationships",
    "secrets",
    "continuityIssues",
  ];
  for (const key of arrays) {
    if (bible[key] != null && !Array.isArray(bible[key])) {
      errors.push(err(key, "Doit être un tableau."));
    }
  }
  if (errors.length) return errors;

  const b = bible as Bible;
  const ids = new Map<string, string>();
  const remember = (id: unknown, path: string) => {
    if (typeof id !== "string" || !ID_RE.test(id)) {
      errors.push(err(path, `Identifiant invalide : ${String(id)}`));
      return;
    }
    if (ids.has(id)) {
      errors.push(err(path, `Identifiant dupliqué « ${id} » (déjà ${ids.get(id)}).`));
    } else {
      ids.set(id, path);
    }
  };

  const checkMeta = (entity: { id?: string; status?: string; sources?: unknown }, path: string) => {
    remember(entity.id, `${path}.id`);
    if (!STATUSES.includes(entity.status as CanonStatus)) {
      errors.push(err(`${path}.status`, `Statut inconnu : ${String(entity.status)}`));
    }
    if (!Array.isArray(entity.sources) || entity.sources.length === 0) {
      if (entity.status === "canon") {
        errors.push(
          err(`${path}.sources`, "Une donnée canon doit avoir au moins une source ou une note de décision."),
        );
      }
    }
  };

  b.books.forEach((book, i) => checkMeta(book, `books[${i}]`));
  b.branches.forEach((branch, i) => checkMeta(branch, `branches[${i}]`));
  b.moments.forEach((moment, i) => checkMeta(moment, `moments[${i}]`));
  b.characters.forEach((character, i) => {
    checkMeta(character, `characters[${i}]`);
    character.states?.forEach((state, j) => {
      if (typeof state.id === "string") remember(state.id, `characters[${i}].states[${j}].id`);
    });
  });
  b.events.forEach((event, i) => checkMeta(event, `events[${i}]`));
  b.relationships.forEach((rel, i) => checkMeta(rel, `relationships[${i}]`));
  b.secrets.forEach((secret, i) => checkMeta(secret, `secrets[${i}]`));
  b.continuityIssues.forEach((issue, i) => checkMeta(issue, `continuityIssues[${i}]`));

  const momentIds = new Set(b.moments.map((m) => m.id));
  const characterIds = new Set(b.characters.map((c) => c.id));
  const branchIds = new Set(b.branches.map((br) => br.id));
  const bookIds = new Set(b.books.map((bk) => bk.id));

  const refMoment = (id: string | null | undefined, path: string) => {
    if (!id) return;
    if (!momentIds.has(id)) errors.push(err(path, `Moment inexistant : ${id}`));
  };
  const refCharacter = (id: string, path: string) => {
    if (!characterIds.has(id)) errors.push(err(path, `Personnage inexistant : ${id}`));
  };
  const refBranch = (id: string | null | undefined, path: string) => {
    if (!id) return;
    if (!branchIds.has(id)) errors.push(err(path, `Branche inexistante : ${id}`));
  };

  b.moments.forEach((moment, i) => {
    if (!bookIds.has(moment.bookId)) {
      errors.push(err(`moments[${i}].bookId`, `Tome inexistant : ${moment.bookId}`));
    }
    refBranch(moment.branchId, `moments[${i}].branchId`);
  });

  b.branches.forEach((branch, i) => {
    refBranch(branch.parentBranchId, `branches[${i}].parentBranchId`);
    refMoment(branch.divergenceMomentId, `branches[${i}].divergenceMomentId`);
  });

  b.characters.forEach((character, i) => {
    character.states?.forEach((state, j) => {
      const p = `characters[${i}].states[${j}]`;
      refMoment(state.validFromMomentId, `${p}.validFromMomentId`);
      refMoment(state.validToMomentId, `${p}.validToMomentId`);
      refBranch(state.branchId, `${p}.branchId`);
      if (
        state.validFromMomentId &&
        state.validToMomentId &&
        getMoment(b, state.validFromMomentId) &&
        getMoment(b, state.validToMomentId) &&
        isBefore(b, state.validToMomentId, state.validFromMomentId)
      ) {
        errors.push(err(`${p}.validToMomentId`, "validTo doit être postérieur à validFrom."));
      }
      if (state.rank != null && state.rank < 1) {
        errors.push(err(`${p}.rank`, "Le rang ne peut pas être négatif ou nul."));
      }
    });
  });

  b.events.forEach((event, i) => {
    refMoment(event.momentId, `events[${i}].momentId`);
    refBranch(event.branchId, `events[${i}].branchId`);
    event.characterIds?.forEach((id, j) => refCharacter(id, `events[${i}].characterIds[${j}]`));
  });

  b.relationships.forEach((rel, i) => {
    refCharacter(rel.fromCharacterId, `relationships[${i}].fromCharacterId`);
    refCharacter(rel.toCharacterId, `relationships[${i}].toCharacterId`);
    refMoment(rel.validFromMomentId, `relationships[${i}].validFromMomentId`);
    refMoment(rel.validToMomentId, `relationships[${i}].validToMomentId`);
    refBranch(rel.branchId, `relationships[${i}].branchId`);
  });

  b.secrets.forEach((secret, i) => {
    refMoment(secret.trueFromMomentId, `secrets[${i}].trueFromMomentId`);
    refMoment(secret.revealedToReaderAtMomentId, `secrets[${i}].revealedToReaderAtMomentId`);
    refBranch(secret.branchId, `secrets[${i}].branchId`);
    secret.knownBy?.forEach((k, j) => {
      refCharacter(k.characterId, `secrets[${i}].knownBy[${j}].characterId`);
      refMoment(k.knownFromMomentId, `secrets[${i}].knownBy[${j}].knownFromMomentId`);
    });
  });

  return errors;
}

export function assertValidBible(data: unknown): Bible {
  const errors = validateBible(data);
  if (errors.length) {
    const msg = errors.map((e) => `${e.path}: ${e.message}`).join("\n");
    throw new Error(`Bible invalide:\n${msg}`);
  }
  return data as Bible;
}
